"use client"
import {
    Box, Button, Dialog, Flex, IconButton, Input, Portal, Slider, Spinner, Text,
} from '@chakra-ui/react';
import { CalculationTooltip } from '../../components/CalculationTooltip';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { lazy, Suspense, useEffect, useReducer, useCallback, useMemo, useSyncExternalStore } from 'react';
import { toast } from 'react-toastify';
import { FiChevronDown, FiTrash2, FiX } from 'react-icons/fi';
import { LuSave } from 'react-icons/lu';
import {
    getLoginRedirectOptions,
    isRecoverableAuthError,
} from '../../utils/auth0Errors';

const CashFlowTimelineChart = lazy(() => import('./StockReverseDcfChart'));

// Extract the most specific error message available from an axios/Auth0 error.
const extractApiErrorMessage = (err, fallback) => {
    const data = err?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data?.error) return data.error;
    if (data?.message) return data.message;
    // Auth0 SDK errors (e.g. from getAccessTokenSilently) expose these fields.
    if (err?.error_description) return err.error_description;
    if (err?.error) return err.error;
    if (err?.message) return err.message;
    return fallback;
};

// Helper functions for number formatting
const formatNumberWithCommas = (value, allowNegative = false) => {
    if (!value && value !== 0) return '';
    const strValue = String(value);
    // Check if negative
    const isNegative = allowNegative && (strValue.startsWith('-') || parseFloat(strValue) < 0);
    // Remove any existing commas and non-numeric characters except decimal point
    let numericValue = strValue.replace(/[^\d.-]/g, '');
    // Remove negative sign temporarily for processing
    if (numericValue.startsWith('-')) {
        numericValue = numericValue.substring(1);
    }
    // Remove any remaining non-numeric characters except decimal
    numericValue = numericValue.replace(/[^\d.]/g, '');
    if (!numericValue || numericValue === '') return isNegative ? '-' : '';
    // Split by decimal point if present
    const parts = numericValue.split('.');
    // Format the integer part with commas
    const formattedInteger = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    parts[0] = isNegative ? '-' + formattedInteger : formattedInteger;
    return parts.join('.');
};

const parseNumberFromFormatted = (value, allowNegative = false) => {
    if (!value && value !== 0) return '';
    // Convert to string first, then remove commas
    let parsed = String(value).replace(/,/g, '');
    // If negative not allowed, remove negative sign
    if (!allowNegative) {
        parsed = parsed.replace(/-/g, '');
    }
    return parsed;
};

// Format number in abbreviated format (e.g., 155,478,908.55 -> $155.48M)
const formatNumberAbbreviated = (value, allowNegative = false) => {
    if (!value && value !== 0) return '';
    const numValue = parseFloat(parseNumberFromFormatted(value, allowNegative));
    if (isNaN(numValue)) return '';
    
    const isNegative = numValue < 0;
    const absValue = Math.abs(numValue);
    
    let formatted;
    if (absValue >= 1e12) {
        // Show two decimal places for Trillions
        formatted = (absValue / 1e12).toFixed(2) + 'T';
    } else if (absValue >= 1e9) {
        // Show two decimal places for Billions
        formatted = (absValue / 1e9).toFixed(2) + 'B';
    } else if (absValue >= 1e6) {
        // Show two decimal places for Millions
        formatted = (absValue / 1e6).toFixed(2) + 'M';
    } else if (absValue >= 1e3) {
        // Show two decimal places for Thousands
        formatted = (absValue / 1e3).toFixed(2) + 'K';
    } else {
        // For values less than 1000, show with 2 decimal places
        formatted = absValue.toFixed(2).replace(/\.?0+$/, '');
    }
    
    // Remove trailing zeros after decimal point (e.g., 155.00M -> 155M, but keep 155.48M)
    formatted = formatted.replace(/\.0+([KMBT])$/, '$1');
    
    return (isNegative ? '-' : '') + '$' + formatted;
};

const formatSharesAbbreviated = (value) => {
    if (!value && value !== 0) return '';
    const numValue = parseFloat(parseNumberFromFormatted(value));
    if (isNaN(numValue)) return '';

    const absValue = Math.abs(numValue);
    let formatted;
    if (absValue >= 1e12) {
        formatted = (absValue / 1e12).toFixed(2) + 'T';
    } else if (absValue >= 1e9) {
        formatted = (absValue / 1e9).toFixed(2) + 'B';
    } else if (absValue >= 1e6) {
        formatted = (absValue / 1e6).toFixed(2) + 'M';
    } else if (absValue >= 1e3) {
        formatted = (absValue / 1e3).toFixed(2) + 'K';
    } else {
        formatted = absValue.toFixed(2).replace(/\.?0+$/, '');
    }

    return formatted.replace(/\.0+([KMBT])$/, '$1');
};

const SHARES_OUTSTANDING_GAAP_LABELS = {
    CommonStockSharesOutstanding: 'Common stock shares outstanding',
    WeightedAverageNumberOfDilutedSharesOutstanding: 'Weighted average diluted shares',
    WeightedAverageNumberOfSharesOutstandingBasic: 'Weighted average basic shares',
    'dei:EntityCommonStockSharesOutstanding': 'Entity common stock shares outstanding',
    'mktw:CapitalStockSharesOutstanding': 'Total common shares outstanding',
    'mktw:CapitalStockSharesIssued': 'Total common shares issued',
};

const getSharesOutstandingSourceInfo = (metadata) => {
    if (!metadata || metadata.source === 'dei') return null;

    const gaapKeyLabel = SHARES_OUTSTANDING_GAAP_LABELS[metadata.gaap_key] || metadata.gaap_key;

    return {
        periodEnd: metadata.period_end,
        form: metadata.form,
        fiscalPeriod: metadata.fiscal_period,
        fiscalYear: metadata.fiscal_year,
        filingUrl: metadata.filing_url,
        gaapKeyLabel,
    };
};

const formatSharePrice = (value) => {
    if (!value && value !== 0) return '';
    const numValue = parseFloat(parseNumberFromFormatted(value));
    if (isNaN(numValue)) return '';
    return numValue.toFixed(2);
};

const parseSharePriceInput = (value) => {
    if (!value && value !== 0) return '';

    const cleaned = String(value)
        .replace(/,/g, '')
        .replace(/[^\d.]/g, '');

    const [integerPart = '', ...decimalParts] = cleaned.split('.');
    if (decimalParts.length === 0) return integerPart;

    const decimalPart = decimalParts.join('').slice(0, 2);
    return `${integerPart}.${decimalPart}`;
};

const toPlainNumberString = (value, fractionDigits = null) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return '';
    return fractionDigits === null
        ? String(numericValue)
        : numericValue.toFixed(fractionDigits);
};

// Parse abbreviated format back to full number string
const parseAbbreviatedNumber = (value, allowNegative = false) => {
    if (!value) return '';
    const str = String(value).trim();
    if (!str) return '';
    
    // Remove $ sign if present
    let cleaned = str.replace(/\$/g, '');
    
    // Check if negative
    const isNegative = cleaned.startsWith('-');
    if (isNegative) {
        cleaned = cleaned.substring(1);
    }
    
    // Extract number and suffix
    const match = cleaned.match(/^([\d.]+)([KMBT]?)$/i);
    if (!match) {
        // If no match, try to parse as regular number
        return parseNumberFromFormatted(str, allowNegative);
    }
    
    const numStr = match[1];
    const suffix = match[2].toUpperCase();
    
    let multiplier = 1;
    if (suffix === 'K') multiplier = 1e3;
    else if (suffix === 'M') multiplier = 1e6;
    else if (suffix === 'B') multiplier = 1e9;
    else if (suffix === 'T') multiplier = 1e12;
    
    const numValue = parseFloat(numStr) * multiplier;
    if (isNaN(numValue)) return '';
    
    const result = (isNegative ? '-' : '') + numValue.toString();
    return allowNegative || !isNegative ? result : '';
};

const parsePositiveDisplayNumber = (value) => {
    const abbreviated = parseAbbreviatedNumber(value);
    return abbreviated || parseNumberFromFormatted(value);
};

const TERMINAL_GROWTH_MIN = -100;
const TERMINAL_GROWTH_MAX = 100;
const TERMINAL_GROWTH_STEP = 0.5;
const TERMINAL_GROWTH_COMFORT_RANGE = { min: -10, max: 10 };
const TERMINAL_GROWTH_EXTREME_NEGATIVE = -20;
const TERMINAL_GROWTH_EXTREME_POSITIVE = 10;
const DEFAULT_FORECAST_YEARS = 8;
const DEFAULT_WACC = 10;
const DEFAULT_TERMINAL_GROWTH = 3;
const FORECAST_YEARS_MIN = 1;
const FORECAST_YEARS_MAX = 20;
const WACC_MIN = 1;
const WACC_MAX = 30;

const clampNumber = (value, min, max) => Math.min(max, Math.max(min, value));

const snapToStep = (value, step) => Math.round(value / step) * step;

const isBlankNumberInput = (value) => value === '' || value === null || value === undefined;

const numericOrDefault = (value, fallback) => {
    if (isBlankNumberInput(value)) return fallback;
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : fallback;
};

const normalizeForecastYearsInput = (value) => {
    if (isBlankNumberInput(value)) return DEFAULT_FORECAST_YEARS;
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return DEFAULT_FORECAST_YEARS;
    return clampNumber(Math.round(numericValue), FORECAST_YEARS_MIN, FORECAST_YEARS_MAX);
};

const normalizeWaccInput = (value) => {
    if (isBlankNumberInput(value)) return DEFAULT_WACC;
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return DEFAULT_WACC;
    return clampNumber(numericValue, WACC_MIN, WACC_MAX);
};

const normalizeTerminalGrowthInput = (value) => {
    if (isBlankNumberInput(value)) return DEFAULT_TERMINAL_GROWTH;
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return DEFAULT_TERMINAL_GROWTH;
    return clampNumber(
        snapToStep(numericValue, TERMINAL_GROWTH_STEP),
        TERMINAL_GROWTH_MIN,
        TERMINAL_GROWTH_MAX
    );
};

const emptyStockPriceSnapshot = {
    data: null,
    error: null,
    loading: false,
};

const STOCK_PRICE_TTL_MS = 5 * 60 * 1000;

const stockPriceRecords = new Map();
const reverseDcfInitialDataRecords = new Map();

const createStockPriceRecord = () => ({
    data: null,
    error: null,
    loading: false,
    promise: null,
    fetchedAt: 0,
    snapshot: emptyStockPriceSnapshot,
    listeners: new Set(),
});

const getStockPriceKey = (apiUrl, ticker) => {
    if (!apiUrl || !ticker) return null;
    return `${apiUrl}|${ticker}`;
};

const getStockPriceRecord = (key) => {
    if (!stockPriceRecords.has(key)) {
        stockPriceRecords.set(key, createStockPriceRecord());
    }
    return stockPriceRecords.get(key);
};

const updateStockPriceSnapshot = (record, nextState) => {
    Object.assign(record, nextState);
    record.snapshot = {
        data: record.data,
        error: record.error,
        loading: record.loading,
    };
    record.listeners.forEach((listener) => listener());
};

const isStockPriceStale = (record) =>
    record.data && Date.now() - record.fetchedAt > STOCK_PRICE_TTL_MS;

const loadStockPrice = (apiUrl, ticker, record) => {
    if (record.promise) return;
    if (record.data && !isStockPriceStale(record)) return;

    const isRevalidation = Boolean(record.data);
    if (!isRevalidation) {
        updateStockPriceSnapshot(record, { loading: true, error: null });
    }
    record.promise = axios
        .get(`${apiUrl}/api/stock-price?ticker=${encodeURIComponent(ticker)}`)
        .then((response) => {
            record.fetchedAt = Date.now();
            updateStockPriceSnapshot(record, {
                data: response.data,
                error: null,
                loading: false,
            });
        })
        .catch((err) => {
            console.log('Could not fetch stock price:', err);
            if (!isRevalidation) {
                updateStockPriceSnapshot(record, {
                    error: err,
                    loading: false,
                });
            }
        })
        .finally(() => {
            record.promise = null;
        });
};

const getStockPriceSnapshot = (apiUrl, ticker) => {
    const key = getStockPriceKey(apiUrl, ticker);
    if (!key) return emptyStockPriceSnapshot;
    return getStockPriceRecord(key).snapshot;
};

const subscribeToStockPrice = (apiUrl, ticker, listener) => {
    const key = getStockPriceKey(apiUrl, ticker);
    if (!key) return () => {};

    const record = getStockPriceRecord(key);
    record.listeners.add(listener);
    loadStockPrice(apiUrl, ticker, record);

    return () => {
        record.listeners.delete(listener);
    };
};

const useStockPrice = (apiUrl, ticker) => {
    const subscribe = useCallback(
        (listener) => subscribeToStockPrice(apiUrl, ticker, listener),
        [apiUrl, ticker]
    );
    const getSnapshot = useCallback(
        () => getStockPriceSnapshot(apiUrl, ticker),
        [apiUrl, ticker]
    );

    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};

const emptyReverseDcfInitialDataSnapshot = {
    data: null,
    error: null,
    loading: false,
};

const createReverseDcfInitialDataRecord = () => ({
    data: null,
    error: null,
    loading: false,
    promise: null,
    snapshot: emptyReverseDcfInitialDataSnapshot,
    listeners: new Set(),
});

const getReverseDcfInitialDataKey = (apiUrl, ticker, year) => {
    if (!apiUrl || !ticker || !year) return null;
    return `${apiUrl}|${ticker}|${year}`;
};

const getReverseDcfInitialDataRecord = (key) => {
    if (!reverseDcfInitialDataRecords.has(key)) {
        reverseDcfInitialDataRecords.set(key, createReverseDcfInitialDataRecord());
    }
    return reverseDcfInitialDataRecords.get(key);
};

const updateReverseDcfInitialDataSnapshot = (record, nextState) => {
    Object.assign(record, nextState);
    record.snapshot = {
        data: record.data,
        error: record.error,
        loading: record.loading,
    };
    record.listeners.forEach((listener) => listener());
};

const loadReverseDcfInitialData = (apiUrl, ticker, record) => {
    if (record.promise || record.data) return;

    updateReverseDcfInitialDataSnapshot(record, { loading: true, error: null });
    record.promise = axios
        .get(`${apiUrl}/api/reverse-dcf/initial-data?ticker=${encodeURIComponent(ticker)}`)
        .then((response) => {
            updateReverseDcfInitialDataSnapshot(record, {
                data: response.data,
                error: null,
                loading: false,
            });
        })
        .catch((err) => {
            console.log('Could not fetch initial FCF data:', err);
            updateReverseDcfInitialDataSnapshot(record, {
                error: err,
                loading: false,
            });
        })
        .finally(() => {
            record.promise = null;
        });
};

const getReverseDcfInitialDataSnapshot = (apiUrl, ticker, year) => {
    const key = getReverseDcfInitialDataKey(apiUrl, ticker, year);
    if (!key) return emptyReverseDcfInitialDataSnapshot;
    return getReverseDcfInitialDataRecord(key).snapshot;
};

const subscribeToReverseDcfInitialData = (apiUrl, ticker, year, listener) => {
    const key = getReverseDcfInitialDataKey(apiUrl, ticker, year);
    if (!key) return () => {};

    const record = getReverseDcfInitialDataRecord(key);
    record.listeners.add(listener);
    loadReverseDcfInitialData(apiUrl, ticker, record);

    return () => {
        record.listeners.delete(listener);
    };
};

const useReverseDcfInitialData = (apiUrl, ticker, year) => {
    const subscribe = useCallback(
        (listener) => subscribeToReverseDcfInitialData(apiUrl, ticker, year, listener),
        [apiUrl, ticker, year]
    );
    const getSnapshot = useCallback(
        () => getReverseDcfInitialDataSnapshot(apiUrl, ticker, year),
        [apiUrl, ticker, year]
    );

    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};

const boldNumberInputProps = {
    fontWeight: 'bold',
    css: {
        MozAppearance: 'textfield',
        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
        },
    },
};

const reverseDcfInitialState = {
    marketCap: '',
    sharePrice: '',
    sharesOutstanding: '',
    netDebt: '',
    fcfTtm: '',
    years: DEFAULT_FORECAST_YEARS,
    wacc: DEFAULT_WACC,
    terminalGrowth: DEFAULT_TERMINAL_GROWTH,
    resultsExpanded: true,
    marketCapExpanded: true,
    focusedInput: null,
    marketCapDraft: '',
    result: null,
    loading: false,
    error: null,
    longTermDebt: null,
    cashAndEquivalents: null,
    cashFlowMetadata: null,
    capexMetadata: null,
    longTermDebtMetadata: null,
    cashMetadata: null,
    operatingCashFlow: null,
    capitalExpenditures: null,
    saveDialogOpen: false,
    savedListOpen: false,
    saveName: '',
    savedAnalyses: [],
    savesLoading: false,
    saveSubmitting: false,
    deletingSaveId: null,
    savesError: '',
    hiddenSeries: {},
    cashFlowView: 'graph',
};

const reverseDcfReducer = (state, action) => {
    switch (action.type) {
        case 'setField': {
            const previousValue = state[action.field];
            const nextValue = typeof action.value === 'function'
                ? action.value(previousValue)
                : action.value;
            return { ...state, [action.field]: nextValue };
        }
        case 'setMarketCapComponent': {
            const nextState = { ...state, [action.field]: action.value };
            const parsedValue = parseFloat(parseNumberFromFormatted(action.value));
            const effectiveSharePrice = parseFloat(parseNumberFromFormatted(action.effectiveSharePrice));
            const effectiveSharesOutstanding = parseFloat(parseNumberFromFormatted(action.effectiveSharesOutstanding));

            if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
                if (action.field === 'sharePrice' || action.field === 'sharesOutstanding') {
                    nextState.marketCap = '';
                } else if (action.field === 'marketCap') {
                    nextState.sharePrice = '';
                }
                return nextState;
            }

            if (action.field === 'sharePrice') {
                if (Number.isFinite(effectiveSharesOutstanding) && effectiveSharesOutstanding > 0) {
                    nextState.marketCap = toPlainNumberString(Math.round(parsedValue * effectiveSharesOutstanding));
                }
            } else if (action.field === 'sharesOutstanding') {
                if (Number.isFinite(effectiveSharePrice) && effectiveSharePrice > 0) {
                    nextState.marketCap = toPlainNumberString(Math.round(effectiveSharePrice * parsedValue));
                }
            } else if (action.field === 'marketCap') {
                if (Number.isFinite(effectiveSharesOutstanding) && effectiveSharesOutstanding > 0) {
                    nextState.sharePrice = toPlainNumberString(parsedValue / effectiveSharesOutstanding, 2);
                }
            }

            return nextState;
        }
        case 'applyAssumptions': {
            const { assumptions } = action;
            if (!assumptions || typeof assumptions !== 'object') return state;

            const nextState = { ...state };
            if (assumptions.marketCap !== undefined) nextState.marketCap = String(assumptions.marketCap);
            if (assumptions.sharePrice !== undefined) nextState.sharePrice = String(assumptions.sharePrice);
            if (assumptions.sharesOutstanding !== undefined) nextState.sharesOutstanding = String(assumptions.sharesOutstanding);
            if (assumptions.netDebt !== undefined) nextState.netDebt = String(assumptions.netDebt);
            if (assumptions.fcfTtm !== undefined) nextState.fcfTtm = String(assumptions.fcfTtm);

            if (assumptions.years !== undefined) {
                const nextYears = Number(assumptions.years);
                if (!Number.isNaN(nextYears)) nextState.years = nextYears;
            }
            if (assumptions.wacc !== undefined) {
                const nextWacc = Number(assumptions.wacc);
                if (!Number.isNaN(nextWacc)) nextState.wacc = nextWacc;
            }
            if (assumptions.terminalGrowth !== undefined) {
                const nextTerminalGrowth = Number(assumptions.terminalGrowth);
                if (!Number.isNaN(nextTerminalGrowth)) nextState.terminalGrowth = nextTerminalGrowth;
            }

            return nextState;
        }
        case 'applyInitialData': {
            const { initialData } = action;
            if (!initialData) return state;

            const operatingCashFlows = initialData.cashFlow['cash_flows_from_operations'];
            const capex = initialData.capex['capital_expenditures'];
            const ltd = initialData.longTermDebt['long_term_debt'];
            const cash = initialData.cash['cash_and_cash_equivalents'];
            const nextState = { ...state };

            if (initialData.cashFlow.metadata) nextState.cashFlowMetadata = initialData.cashFlow.metadata;
            if (initialData.capex.metadata) nextState.capexMetadata = initialData.capex.metadata;
            if (initialData.longTermDebt?.metadata) nextState.longTermDebtMetadata = initialData.longTermDebt.metadata;
            if (initialData.cash?.metadata) nextState.cashMetadata = initialData.cash.metadata;
            if (typeof operatingCashFlows === 'number') nextState.operatingCashFlow = operatingCashFlows;
            if (typeof capex === 'number') nextState.capitalExpenditures = capex;
            if (typeof operatingCashFlows === 'number' && typeof capex === 'number') {
                nextState.fcfTtm = operatingCashFlows - capex;
            }
            if (typeof ltd === 'number') {
                nextState.longTermDebt = ltd;
            } else if (typeof cash === 'number') {
                nextState.longTermDebt = 0;
            }
            if (typeof cash === 'number') nextState.cashAndEquivalents = cash;
            if (state.netDebt === '') {
                if (typeof cash === 'number') {
                    const effectiveDebt = typeof ltd === 'number' ? ltd : 0;
                    nextState.netDebt = formatNumberWithCommas((effectiveDebt - cash).toString(), true);
                } else if (typeof ltd === 'number') {
                    nextState.netDebt = formatNumberWithCommas(ltd.toString(), true);
                }
            }

            return nextState;
        }
        case 'calculationInvalid':
            return { ...state, result: null, error: null };
        case 'calculationStarted':
            return { ...state, loading: true, error: null };
        case 'calculationSucceeded':
            return { ...state, result: action.result, loading: false };
        case 'calculationFailed':
            return { ...state, error: action.error, result: null, loading: false };
        default:
            return state;
    }
};

function ReverseDcfMarketCapSection({
    stockPrice,
    effectiveSharePrice,
    effectiveSharesOutstanding,
    effectiveMarketCap,
    marketCapExpanded,
    marketCapDraft,
    focusedInput,
    dispatchReverseDcf,
    priceLoading,
    priceUnavailable,
}) {
    const formattedMarketCap = effectiveMarketCap ? formatNumberAbbreviated(effectiveMarketCap) : '';

    const toggleMarketCapExpanded = () => dispatchReverseDcf({
        type: 'setField',
        field: 'marketCapExpanded',
        value: (prev) => !prev,
    });

    const marketCapTooltip = stockPrice?.close != null && stockPrice?.shares_outstanding != null && stockPrice?.market_cap != null ? (
        <Box
            display="inline-flex"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
        >
            <CalculationTooltip
                title="Market Cap"
                value1={{
                    label: "Latest Close",
                    value: stockPrice.close
                }}
                value2={{
                    label: "Shares Outstanding",
                    value: stockPrice.shares_outstanding,
                    operation: "multiply"
                }}
                result={stockPrice.market_cap}
            />
        </Box>
    ) : null;

    const marketCapChevron = (
        <Box
            as={FiChevronDown}
            fontSize="18px"
            transition="transform 0.2s ease"
            transform={marketCapExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}
            aria-hidden="true"
            flexShrink={0}
        />
    );

    const commitMarketCapDraft = () => {
        const parsed = parsePositiveDisplayNumber(marketCapDraft);
        dispatchReverseDcf({
            type: 'setMarketCapComponent',
            field: 'marketCap',
            value: parsed,
            effectiveSharePrice,
            effectiveSharesOutstanding,
        });
        dispatchReverseDcf({ type: 'setField', field: 'focusedInput', value: null });
        dispatchReverseDcf({ type: 'setField', field: 'marketCapDraft', value: '' });
    };

    const marketCapInput = (
        <Input
            value={focusedInput === 'marketCap'
                ? marketCapDraft
                : formattedMarketCap}
            onChange={(e) => {
                dispatchReverseDcf({ type: 'setField', field: 'marketCapDraft', value: e.target.value });
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') {
                    e.currentTarget.blur();
                }
            }}
            onFocus={() => {
                dispatchReverseDcf({ type: 'setField', field: 'focusedInput', value: 'marketCap' });
                dispatchReverseDcf({
                    type: 'setField',
                    field: 'marketCapDraft',
                    value: effectiveMarketCap ? formatNumberWithCommas(effectiveMarketCap) : '',
                });
            }}
            onBlur={commitMarketCapDraft}
            placeholder="Share price * Shares outstanding"
            type="text"
            textAlign={{ base: 'left', sm: 'right' }}
        />
    );

    return (
        <Flex
            direction="column"
            gap={3}
            p={3}
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
        >
            {marketCapExpanded ? (
                <>
                    <Flex
                        alignItems="center"
                        justifyContent="space-between"
                        gap={3}
                        role="button"
                        tabIndex={0}
                        aria-expanded={marketCapExpanded}
                        onClick={toggleMarketCapExpanded}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleMarketCapExpanded();
                            }
                        }}
                        cursor="pointer"
                    >
                        <Flex alignItems="center" gap={2} minW={0}>
                            <Text fontWeight="bold">Market Cap Calculation</Text>
                            {marketCapTooltip}
                        </Flex>
                        {marketCapChevron}
                    </Flex>
                    <Flex
                        alignItems={{ base: 'stretch', sm: 'center' }}
                        justifyContent="space-between"
                        gap={2}
                        direction={{ base: 'column', sm: 'row' }}
                    >
                        <Text color="gray.700" flexShrink={0}>Share Price ($)</Text>
                        <Input
                            value={focusedInput === 'sharePrice'
                                ? formatNumberWithCommas(effectiveSharePrice)
                                : formatSharePrice(effectiveSharePrice)}
                            onChange={(e) => {
                                const parsed = parseSharePriceInput(e.target.value);
                                dispatchReverseDcf({
                                    type: 'setMarketCapComponent',
                                    field: 'sharePrice',
                                    value: parsed,
                                    effectiveSharePrice,
                                    effectiveSharesOutstanding,
                                });
                            }}
                            onFocus={() => dispatchReverseDcf({ type: 'setField', field: 'focusedInput', value: 'sharePrice' })}
                            onBlur={() => dispatchReverseDcf({ type: 'setField', field: 'focusedInput', value: null })}
                            placeholder="150.00"
                            type="text"
                            textAlign={{ base: 'left', sm: 'right' }}
                        />
                    </Flex>
                    <Flex
                        alignItems={{ base: 'stretch', sm: 'center' }}
                        justifyContent="space-between"
                        gap={2}
                        direction={{ base: 'column', sm: 'row' }}
                    >
                        <Flex alignItems="center" gap={2} flexShrink={0}>
                            <Text color="gray.700">× Shares Out</Text>
                            {stockPrice?.shares_outstanding != null && getSharesOutstandingSourceInfo(stockPrice.shares_outstanding_metadata) && (
                                <CalculationTooltip
                                    title="Shares outstanding"
                                    breakdown={stockPrice.shares_outstanding_metadata.share_classes?.map((shareClass) => ({
                                        label: shareClass.label,
                                        value: shareClass.value,
                                        formatAs: 'number',
                                    }))}
                                    value1={!stockPrice.shares_outstanding_metadata.share_classes?.length ? {
                                        label: SHARES_OUTSTANDING_GAAP_LABELS[
                                            stockPrice.shares_outstanding_metadata.gaap_key
                                        ] || 'Shares outstanding',
                                        value: stockPrice.shares_outstanding,
                                        formatAs: 'number',
                                    } : undefined}
                                    result={stockPrice.shares_outstanding_metadata.share_classes?.length
                                        ? stockPrice.shares_outstanding
                                        : undefined}
                                    resultFormatAs="number"
                                    sourceInfo={getSharesOutstandingSourceInfo(stockPrice.shares_outstanding_metadata)}
                                />
                            )}
                        </Flex>
                        <Input
                            value={focusedInput === 'sharesOutstanding'
                                ? formatNumberWithCommas(effectiveSharesOutstanding)
                                : formatSharesAbbreviated(effectiveSharesOutstanding)}
                            onChange={(e) => {
                                const parsed = parsePositiveDisplayNumber(e.target.value);
                                dispatchReverseDcf({
                                    type: 'setMarketCapComponent',
                                    field: 'sharesOutstanding',
                                    value: parsed,
                                    effectiveSharePrice,
                                    effectiveSharesOutstanding,
                                });
                            }}
                            onFocus={() => dispatchReverseDcf({ type: 'setField', field: 'focusedInput', value: 'sharesOutstanding' })}
                            onBlur={() => dispatchReverseDcf({ type: 'setField', field: 'focusedInput', value: null })}
                            placeholder="15.5B"
                            type="text"
                            textAlign={{ base: 'left', sm: 'right' }}
                        />
                    </Flex>
                    <Box borderTop="1px solid" borderColor="gray.200" />
                    <Flex
                        alignItems={{ base: 'stretch', sm: 'center' }}
                        justifyContent="space-between"
                        gap={2}
                        direction={{ base: 'column', sm: 'row' }}
                    >
                        <Text fontWeight="bold" flexShrink={0}>= Market Cap ($)</Text>
                        {marketCapInput}
                    </Flex>
                </>
            ) : (
                <Flex
                    alignItems="center"
                    justifyContent="space-between"
                    gap={3}
                    role="button"
                    tabIndex={0}
                    aria-expanded={marketCapExpanded}
                    onClick={toggleMarketCapExpanded}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleMarketCapExpanded();
                        }
                    }}
                    cursor="pointer"
                >
                    <Flex alignItems="center" gap={2} flexShrink={0}>
                        <Text fontWeight="bold">Market Cap ($)</Text>
                        {marketCapTooltip}
                    </Flex>
                    <Flex alignItems="center" gap={2} flex="1" minW={0}>
                        {marketCapInput}
                        {marketCapChevron}
                    </Flex>
                </Flex>
            )}
            {priceLoading && (
                <Text fontSize="sm" color="gray.500">
                    Loading stock price…
                </Text>
            )}
            {priceUnavailable && (
                <Text fontSize="sm" color="orange.600">
                    Current stock price is unavailable. Enter it manually.
                </Text>
            )}
        </Flex>
    );
}

function ReverseDcfInputs({
    initialLoading,
    isAuthenticated,
    loginWithRedirect,
    openSavedAnalyses,
    savesLoading,
    ticker,
    stockPrice,
    effectiveSharePrice,
    effectiveSharesOutstanding,
    effectiveMarketCap,
    marketCapExpanded,
    marketCapDraft,
    focusedInput,
    dispatchReverseDcf,
    priceLoading,
    priceUnavailable,
    netDebt,
    longTermDebt,
    cashAndEquivalents,
    longTermDebtMetadata,
    cashMetadata,
    fcfTtm,
    operatingCashFlow,
    capitalExpenditures,
    cashFlowMetadata,
    years,
    wacc,
    terminalGrowth,
    labelStyles,
    isTerminalGrowthOutsideComfortRange,
    isTerminalGrowthExtreme,
    error,
}) {
    return (
        <Flex
            flexDirection="column"
            width={{ base: '100%', lg: '45%' }}
            minW={0}
            gap={4}
        >
            {initialLoading && <Spinner size='lg' color='green.500' />}

            <Flex alignItems="center" gap={2} flexWrap="wrap">
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                        if (!isAuthenticated) {
                            void loginWithRedirect(getLoginRedirectOptions());
                            return;
                        }
                        void openSavedAnalyses();
                    }}
                    loading={isAuthenticated && savesLoading}
                >
                    Load Saved Assumptions
                </Button>
                <Button
                    size="sm"
                    colorPalette="green"
                    onClick={() => {
                        if (!isAuthenticated) {
                            void loginWithRedirect(getLoginRedirectOptions());
                            return;
                        }
                        dispatchReverseDcf({ type: 'setField', field: 'savesError', value: '' });
                        dispatchReverseDcf({ type: 'setField', field: 'saveDialogOpen', value: true });
                    }}
                >
                    <LuSave />
                    Save Assumptions
                </Button>
                <Button
                    as={RouterLink}
                    to={`/stock/${String(ticker || '').toLowerCase()}/analysis`}
                    size="sm"
                    variant="outline"
                    colorPalette="green"
                >
                    Build your own DCF
                </Button>
            </Flex>

            <ReverseDcfMarketCapSection
                stockPrice={stockPrice}
                effectiveSharePrice={effectiveSharePrice}
                effectiveSharesOutstanding={effectiveSharesOutstanding}
                effectiveMarketCap={effectiveMarketCap}
                marketCapExpanded={marketCapExpanded}
                marketCapDraft={marketCapDraft}
                focusedInput={focusedInput}
                dispatchReverseDcf={dispatchReverseDcf}
                priceLoading={priceLoading}
                priceUnavailable={priceUnavailable}
            />

            <Flex direction="column" gap={2}>
                <Flex alignItems="center" gap={2}>
                    <Text fontWeight="bold">Net Debt ($)</Text>
                    {(longTermDebt !== null || cashAndEquivalents !== null) && (
                        <CalculationTooltip
                            title="Net debt"
                            value1={{
                                label: "Long-term debt",
                                value: longTermDebt
                            }}
                            value2={{
                                label: "Cash & cash equivalents",
                                value: cashAndEquivalents,
                                operation: "subtract"
                            }}
                            result={longTermDebt !== null && cashAndEquivalents !== null
                                ? longTermDebt - cashAndEquivalents
                                : longTermDebt !== null
                                ? longTermDebt
                                : cashAndEquivalents !== null
                                ? -cashAndEquivalents
                                : null
                            }
                            sourceInfo={longTermDebtMetadata ? {
                                periodEnd: longTermDebtMetadata.period_end,
                                form: longTermDebtMetadata.form,
                                fiscalPeriod: longTermDebtMetadata.fiscal_period,
                                fiscalYear: longTermDebtMetadata.fiscal_year,
                                filingUrl: longTermDebtMetadata.filing_url
                            } : cashMetadata ? {
                                periodEnd: cashMetadata.period_end,
                                form: cashMetadata.form,
                                fiscalPeriod: cashMetadata.fiscal_period,
                                fiscalYear: cashMetadata.fiscal_year,
                                filingUrl: cashMetadata.filing_url
                            } : null}
                        />
                    )}
                </Flex>
                <Input
                    value={focusedInput === 'netDebt'
                        ? formatNumberWithCommas(netDebt, true)
                        : netDebt ? formatNumberAbbreviated(netDebt, true) : ''}
                    onChange={(e) => {
                        const abbreviated = parseAbbreviatedNumber(e.target.value, true);
                        const parsed = abbreviated || parseNumberFromFormatted(e.target.value, true);
                        dispatchReverseDcf({ type: 'setField', field: 'netDebt', value: parsed });
                    }}
                    onFocus={() => dispatchReverseDcf({ type: 'setField', field: 'focusedInput', value: 'netDebt' })}
                    onBlur={() => dispatchReverseDcf({ type: 'setField', field: 'focusedInput', value: null })}
                    placeholder="Debt minus cash & cash equivalents"
                    type="text"
                />
            </Flex>

            <Flex direction="column" gap={2}>
                <Flex alignItems="center" gap={2}>
                    <Text fontWeight="bold">Free Cash Flow ($)</Text>
                    {(operatingCashFlow !== null || capitalExpenditures !== null) && (
                        <CalculationTooltip
                            title="Free Cash Flow"
                            value1={{
                                label: "Operating Cash Flow",
                                value: operatingCashFlow
                            }}
                            value2={{
                                label: "Capital Expenditures",
                                value: capitalExpenditures,
                                operation: "subtract"
                            }}
                            result={operatingCashFlow !== null && capitalExpenditures !== null
                                ? operatingCashFlow - capitalExpenditures
                                : null
                            }
                            sourceInfo={cashFlowMetadata ? {
                                periodEnd: cashFlowMetadata.period_end,
                                form: cashFlowMetadata.form,
                                fiscalPeriod: cashFlowMetadata.fiscal_period,
                                fiscalYear: cashFlowMetadata.fiscal_year,
                                filingUrl: cashFlowMetadata.filing_url
                            } : null}
                        />
                    )}
                </Flex>
                <Input
                    value={focusedInput === 'fcfTtm'
                        ? formatNumberWithCommas(fcfTtm, true)
                        : fcfTtm ? formatNumberAbbreviated(fcfTtm, true) : ''}
                    onChange={(e) => {
                        const abbreviated = parseAbbreviatedNumber(e.target.value, true);
                        const parsed = abbreviated || parseNumberFromFormatted(e.target.value, true);
                        dispatchReverseDcf({ type: 'setField', field: 'fcfTtm', value: parsed });
                    }}
                    onFocus={() => dispatchReverseDcf({ type: 'setField', field: 'focusedInput', value: 'fcfTtm' })}
                    onBlur={() => dispatchReverseDcf({ type: 'setField', field: 'focusedInput', value: null })}
                    placeholder="operating cash flow - capital expenditures"
                    type="text"
                />
            </Flex>

            <Flex direction="column" gap={2}>
                <Flex alignItems="center" justifyContent="space-between" gap={2}>
                    <Text fontWeight="bold">Forecast Years</Text>
                    <Input
                        type="text"
                        inputMode="numeric"
                        width="72px"
                        size="sm"
                        textAlign="right"
                        value={years}
                        {...boldNumberInputProps}
                        onChange={(e) => {
                            dispatchReverseDcf({ type: 'setField', field: 'years', value: e.target.value });
                        }}
                        onBlur={() => {
                            dispatchReverseDcf({
                                type: 'setField',
                                field: 'years',
                                value: normalizeForecastYearsInput(years),
                            });
                        }}
                    />
                </Flex>
                <Slider.Root
                    value={[numericOrDefault(years, DEFAULT_FORECAST_YEARS)]}
                    min={FORECAST_YEARS_MIN}
                    max={FORECAST_YEARS_MAX}
                    onValueChange={(e) => dispatchReverseDcf({ type: 'setField', field: 'years', value: e.value[0] })}
                >
                    <Slider.Control>
                        <Slider.Track bg='blue.100'>
                            <Slider.Range bg='blue.500' />
                        </Slider.Track>
                        <Slider.Thumb boxSize={6} />
                    </Slider.Control>
                </Slider.Root>
            </Flex>

            <Flex direction="column" gap={2}>
                <Flex alignItems="center" justifyContent="space-between" gap={2}>
                    <Text fontWeight="bold">WACC (%)</Text>
                    <Input
                        type="text"
                        inputMode="decimal"
                        width="72px"
                        size="sm"
                        textAlign="right"
                        value={wacc}
                        {...boldNumberInputProps}
                        onChange={(e) => {
                            dispatchReverseDcf({ type: 'setField', field: 'wacc', value: e.target.value });
                        }}
                        onBlur={() => {
                            dispatchReverseDcf({
                                type: 'setField',
                                field: 'wacc',
                                value: normalizeWaccInput(wacc),
                            });
                        }}
                    />
                </Flex>
                <Slider.Root
                    value={[numericOrDefault(wacc, DEFAULT_WACC)]}
                    min={WACC_MIN}
                    max={WACC_MAX}
                    onValueChange={(e) => dispatchReverseDcf({ type: 'setField', field: 'wacc', value: e.value[0] })}
                >
                    <Slider.Control>
                        <Slider.Track bg='orange.100'>
                            <Slider.Range bg='orange.500' />
                        </Slider.Track>
                        <Slider.Thumb boxSize={6} />
                    </Slider.Control>
                    <Slider.MarkerGroup>
                        <Slider.Marker value={5} {...labelStyles}>5%</Slider.Marker>
                        <Slider.Marker value={10} {...labelStyles}>10%</Slider.Marker>
                        <Slider.Marker value={15} {...labelStyles}>15%</Slider.Marker>
                        <Slider.Marker value={20} {...labelStyles}>20%</Slider.Marker>
                    </Slider.MarkerGroup>
                </Slider.Root>
            </Flex>

            <Flex direction="column" gap={2} mt={4}>
                <Flex alignItems="center" justifyContent="space-between" gap={2}>
                    <Text fontWeight="bold">Terminal Growth Rate (%)</Text>
                    <Input
                        type="text"
                        inputMode="decimal"
                        width="72px"
                        size="sm"
                        textAlign="right"
                        value={terminalGrowth}
                        {...boldNumberInputProps}
                        onChange={(e) => {
                            dispatchReverseDcf({ type: 'setField', field: 'terminalGrowth', value: e.target.value });
                        }}
                        onBlur={() => {
                            dispatchReverseDcf({
                                type: 'setField',
                                field: 'terminalGrowth',
                                value: normalizeTerminalGrowthInput(terminalGrowth),
                            });
                        }}
                    />
                </Flex>
                <Slider.Root
                    value={[numericOrDefault(terminalGrowth, DEFAULT_TERMINAL_GROWTH)]}
                    min={TERMINAL_GROWTH_MIN}
                    max={TERMINAL_GROWTH_MAX}
                    step={TERMINAL_GROWTH_STEP}
                    onValueChange={(e) => dispatchReverseDcf({ type: 'setField', field: 'terminalGrowth', value: e.value[0] })}
                >
                    <Slider.Control>
                        <Slider.Track bg='purple.100'>
                            <Slider.Range bg='purple.500' />
                        </Slider.Track>
                        <Slider.Thumb boxSize={6} />
                    </Slider.Control>
                    <Slider.MarkerGroup>
                        <Slider.Marker value={0} {...labelStyles}>0%</Slider.Marker>
                    </Slider.MarkerGroup>
                </Slider.Root>
                <Text fontSize="sm" color="gray.600">
                    Range: -100% to +100%. Use negative terminal growth to model shrinking or melting businesses.
                </Text>
                {isTerminalGrowthOutsideComfortRange && (
                    <Box
                        bg="yellow.50"
                        borderLeft="4px solid"
                        borderColor="yellow.400"
                        borderRadius="md"
                        p={3}
                    >
                        <Text fontSize="sm" color="yellow.900">
                            Values outside -10% to +10% assume unusually sharp decline or expansion. Double-check that your thesis
                            supports a long-term rate this far from steady state.
                        </Text>
                    </Box>
                )}
                {isTerminalGrowthExtreme && (
                    <Box
                        bg="red.50"
                        borderLeft="4px solid"
                        borderColor="red.400"
                        borderRadius="md"
                        p={3}
                    >
                        <Text fontSize="sm" color="red.900">
                            Less than -20% or more than 10% implies an extreme scenario where terminal value becomes highly sensitive.
                            Make sure WACC comfortably exceeds the growth assumption.
                        </Text>
                    </Box>
                )}
            </Flex>

            {error && (
                <Box bg="red.100" p={3} borderRadius="md" mt={4}>
                    <Text color="red.700">{error}</Text>
                </Box>
            )}
        </Flex>
    );
}

function ReverseDcfResults({
    loading,
    result,
    resultsExpanded,
    dispatchReverseDcf,
    filteredChartData,
    hiddenSeries,
    toggleSeries,
    chartSectionBounds,
    historicalFcfDot,
    historicalFcfActiveDot,
    cashFlowView,
}) {
    return (
        <Flex
            flexDirection="column"
            justifyContent="flex-start"
            width={{ base: '100%', lg: '50%' }}
            minW={0}
        >
            {loading && <Spinner size='xl' color='blue.500' />}

            {result && (
                <>
                    <Flex direction="column" width={'100%'} gap={4} mb={6}>
                        <Flex
                            position="relative"
                            alignItems={{ base: 'stretch', md: 'center' }}
                            justifyContent="space-between"
                            direction={{ base: 'column', md: 'row' }}
                            gap={{ base: 3, md: 2 }}
                            bg="green.100"
                            p={4}
                            borderRadius="md"
                            role="button"
                            tabIndex={0}
                            onClick={() => dispatchReverseDcf({
                                type: 'setField',
                                field: 'resultsExpanded',
                                value: (prev) => !prev,
                            })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    dispatchReverseDcf({
                                        type: 'setField',
                                        field: 'resultsExpanded',
                                        value: (prev) => !prev,
                                    });
                                }
                            }}
                            cursor="pointer"
                        >
                            <Box
                                as={FiChevronDown}
                                display={{ base: 'block', md: 'none' }}
                                position="absolute"
                                top="4"
                                right="4"
                                fontSize="20px"
                                transition="transform 0.2s ease"
                                transform={resultsExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}
                                aria-hidden="true"
                            />
                            <Flex
                                direction={{ base: 'column', md: 'row' }}
                                alignItems={{ base: 'flex-start', md: 'center' }}
                                gap={{ base: 1, md: 2 }}
                                flex={1}
                                minW={0}
                                pr={{ base: 8, md: 0 }}
                            >
                                <Text fontSize="2xl" fontWeight="bold">Implied FCF Growth Rate:</Text>
                                <Text fontSize="3xl" fontWeight="bold" color="green.700">
                                    {(result.implied_growth * 100).toFixed(2)}%
                                </Text>
                            </Flex>
                            <Box
                                as={FiChevronDown}
                                display={{ base: 'none', md: 'block' }}
                                fontSize="20px"
                                transition="transform 0.2s ease"
                                transform={resultsExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}
                                aria-hidden="true"
                                flexShrink={0}
                            />
                        </Flex>

                        <Box
                            overflow="hidden"
                            transition="max-height 0.3s ease-out, opacity 0.3s ease-out"
                            maxHeight={resultsExpanded ? "1000px" : "0px"}
                            opacity={resultsExpanded ? 1 : 0}
                        >
                            <Flex direction="column" gap={3} mt={2}>
                                <Flex alignItems="center" justifyContent="space-between" bg="blue.50" p={2} borderRadius="md">
                                    <Text fontWeight="bold">Target Enterprise Value:</Text>
                                    <Text fontWeight="bold">${result.enterprise_value_target.toLocaleString()}</Text>
                                </Flex>
                                <Flex alignItems="center" justifyContent="space-between" bg="gray.50" p={2} borderRadius="md">
                                    <Text fontWeight="bold">PV of Forecast FCFs:</Text>
                                    <Text fontWeight="bold">${result.pv_forecast_total.toLocaleString()}</Text>
                                </Flex>
                                <Flex alignItems="center" justifyContent="space-between" bg="gray.50" p={2} borderRadius="md">
                                    <Text fontWeight="bold">Terminal Value:</Text>
                                    <Text fontWeight="bold">${result.terminal_value.toLocaleString()}</Text>
                                </Flex>
                                <Flex alignItems="center" justifyContent="space-between" bg="gray.50" p={2} borderRadius="md">
                                    <Text fontWeight="bold">PV of Terminal Value:</Text>
                                    <Text fontWeight="bold">${result.pv_terminal_value.toLocaleString()}</Text>
                                </Flex>
                                <Flex alignItems="center" justifyContent="space-between" bg="blue.100" p={2} borderRadius="md">
                                    <Text fontWeight="bold">Total PV:</Text>
                                    <Text fontWeight="bold">${result.pv_total.toLocaleString()}</Text>
                                </Flex>
                            </Flex>
                        </Box>
                    </Flex>

                    <Box width="100%">
                        <Flex
                            alignItems={{ base: 'stretch', sm: 'flex-start' }}
                            justifyContent="space-between"
                            direction={{ base: 'column', sm: 'row' }}
                            gap={3}
                            mb={4}
                            width="100%"
                        >
                            <Box>
                                <Text fontSize="lg" fontWeight="bold">
                                    Cash Flow Timeline
                                </Text>
                                <Text fontSize="sm" color="gray.500">
                                    Historical → Projection → Terminal (20-year detail)
                                </Text>
                            </Box>
                            <Flex gap={2} alignSelf={{ base: 'stretch', sm: 'flex-start' }}>
                                <Button
                                    size="sm"
                                    variant={cashFlowView === 'graph' ? 'solid' : 'outline'}
                                    onClick={() => dispatchReverseDcf({ type: 'setField', field: 'cashFlowView', value: 'graph' })}
                                    flex={{ base: 1, sm: 'initial' }}
                                >
                                    Graph
                                </Button>
                                <Button
                                    size="sm"
                                    variant={cashFlowView === 'table' ? 'solid' : 'outline'}
                                    onClick={() => dispatchReverseDcf({ type: 'setField', field: 'cashFlowView', value: 'table' })}
                                    flex={{ base: 1, sm: 'initial' }}
                                >
                                    Table
                                </Button>
                            </Flex>
                        </Flex>

                        {cashFlowView === 'table' ? (
                            <Box overflowX="auto" mb={8}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ backgroundColor: '#F7FAFC', textAlign: 'left', padding: '8px', borderBottom: '2px solid #CBD5E0' }}>Year</th>
                                            <th style={{ backgroundColor: '#F7FAFC', textAlign: 'right', padding: '8px', borderBottom: '2px solid #CBD5E0' }}>FCF</th>
                                            <th style={{ backgroundColor: '#F7FAFC', textAlign: 'right', padding: '8px', borderBottom: '2px solid #CBD5E0' }}>PV(FCF)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.fcf_forecast.map((flow) => (
                                            <tr key={flow.year} style={{ borderTop: '1px solid #EDF2F7' }}>
                                                <td style={{ padding: '8px' }}>{flow.year}</td>
                                                <td style={{ padding: '8px', textAlign: 'right' }}>${flow.fcf.toLocaleString()}</td>
                                                <td style={{ padding: '8px', textAlign: 'right' }}>${flow.pv_fcf.toLocaleString()}</td>
                                            </tr>
                                        ))}
                                        {(result.terminal_value != null || result.pv_terminal_value != null) && (
                                            <tr
                                                data-testid="terminal-value-row"
                                                style={{
                                                    borderTop: '2px solid #CBD5E0',
                                                    backgroundColor: '#F7FAFC',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                <td style={{ padding: '8px' }}>Terminal Value</td>
                                                <td style={{ padding: '8px', textAlign: 'right' }}>
                                                    {result.terminal_value != null
                                                        ? `$${Number(result.terminal_value).toLocaleString()}`
                                                        : '—'}
                                                </td>
                                                <td style={{ padding: '8px', textAlign: 'right' }}>
                                                    {result.pv_terminal_value != null
                                                        ? `$${Number(result.pv_terminal_value).toLocaleString()}`
                                                        : '—'}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </Box>
                        ) : (
                            <Suspense
                                fallback={(
                                    <Box width="100%" height="420px" display="flex" alignItems="center" justifyContent="center">
                                        <Spinner size="lg" color="blue.500" />
                                    </Box>
                                )}
                            >
                                <CashFlowTimelineChart
                                    filteredChartData={filteredChartData}
                                    result={result}
                                    hiddenSeries={hiddenSeries}
                                    toggleSeries={toggleSeries}
                                    chartSectionBounds={chartSectionBounds}
                                    historicalFcfDot={historicalFcfDot}
                                    historicalFcfActiveDot={historicalFcfActiveDot}
                                    formatNumberAbbreviated={formatNumberAbbreviated}
                                />
                            </Suspense>
                        )}
                    </Box>
                </>
            )}
        </Flex>
    );
}

function SaveAssumptionsDialog({
    saveDialogOpen,
    dispatchReverseDcf,
    saveName,
    savesError,
    saveSubmitting,
    saveCurrentAssumptions,
}) {
    return (
        <Dialog.Root
            open={saveDialogOpen}
            onOpenChange={(details) => dispatchReverseDcf({ type: 'setField', field: 'saveDialogOpen', value: details.open })}
        >
            <Portal>
                <Dialog.Backdrop />
                <Dialog.Positioner>
                    <Dialog.Content>
                        <Dialog.Header>
                            <Dialog.Title>Save Assumptions</Dialog.Title>
                            <Dialog.CloseTrigger asChild>
                                <IconButton
                                    aria-label="Close"
                                    variant="ghost"
                                    size="sm"
                                    position="absolute"
                                    top="3"
                                    right="3"
                                >
                                    <FiX />
                                </IconButton>
                            </Dialog.CloseTrigger>
                        </Dialog.Header>
                        <Dialog.Body>
                            <Flex direction="column" gap={3}>
                                <Text fontSize="sm" color="gray.600">
                                    Name this reverse DCF scenario.
                                </Text>
                                <Input
                                    value={saveName}
                                    onChange={(e) => dispatchReverseDcf({ type: 'setField', field: 'saveName', value: e.target.value })}
                                    maxLength={100}
                                    placeholder="e.g. Bull case, Conservative scenario"
                                />
                                {savesError && (
                                    <Text fontSize="sm" color="red.600">{savesError}</Text>
                                )}
                            </Flex>
                        </Dialog.Body>
                        <Dialog.Footer>
                            <Button
                                variant="outline"
                                onClick={() => dispatchReverseDcf({ type: 'setField', field: 'saveDialogOpen', value: false })}
                                disabled={saveSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                colorPalette="green"
                                onClick={saveCurrentAssumptions}
                                loading={saveSubmitting}
                            >
                                Save
                            </Button>
                        </Dialog.Footer>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    );
}

function SavedAnalysesDialog({
    savedListOpen,
    dispatchReverseDcf,
    savesLoading,
    savesError,
    savedAnalyses,
    ticker,
    loadSavedAnalysis,
    formatSavedAnalysisLabel,
    deleteSavedAnalysis,
    deletingSaveId,
    fetchSavedAnalyses,
}) {
    return (
        <Dialog.Root
            open={savedListOpen}
            onOpenChange={(details) => dispatchReverseDcf({ type: 'setField', field: 'savedListOpen', value: details.open })}
        >
            <Portal>
                <Dialog.Backdrop />
                <Dialog.Positioner>
                    <Dialog.Content>
                        <Dialog.Header>
                            <Dialog.Title>Load Saved Analysis</Dialog.Title>
                            <Dialog.CloseTrigger asChild>
                                <IconButton
                                    aria-label="Close"
                                    variant="ghost"
                                    size="sm"
                                    position="absolute"
                                    top="3"
                                    right="3"
                                >
                                    <FiX />
                                </IconButton>
                            </Dialog.CloseTrigger>
                        </Dialog.Header>
                        <Dialog.Body>
                            <Flex direction="column" gap={3}>
                                {savesLoading && <Spinner size="md" color="blue.500" />}
                                {savesError && (
                                    <Text fontSize="sm" color="red.600">{savesError}</Text>
                                )}
                                {!savesLoading && savedAnalyses.length === 0 && (
                                    <Text color="gray.600">No saved analyses for {ticker?.toUpperCase()}.</Text>
                                )}
                                {savedAnalyses.map((saved) => (
                                    <Flex
                                        key={saved.id}
                                        alignItems="center"
                                        justifyContent="space-between"
                                        gap={3}
                                        border="1px solid"
                                        borderColor="gray.200"
                                        borderRadius="md"
                                        p={3}
                                    >
                                        <Button
                                            variant="ghost"
                                            justifyContent="flex-start"
                                            flex="1"
                                            onClick={() => loadSavedAnalysis(saved)}
                                            minW={0}
                                        >
                                            <Flex direction="column" alignItems="flex-start" minW={0}>
                                                <Text truncate maxW="100%">{formatSavedAnalysisLabel(saved)}</Text>
                                                <Text fontSize="xs" color="gray.500">
                                                    {saved.updated_at
                                                        ? `Updated ${new Date(saved.updated_at).toLocaleString()}`
                                                        : 'No updated date'}
                                                </Text>
                                            </Flex>
                                        </Button>
                                        <IconButton
                                            aria-label={`Delete ${formatSavedAnalysisLabel(saved)}`}
                                            variant="ghost"
                                            colorPalette="red"
                                            size="sm"
                                            onClick={() => deleteSavedAnalysis(saved)}
                                            loading={deletingSaveId === saved.id}
                                        >
                                            <FiTrash2 />
                                        </IconButton>
                                    </Flex>
                                ))}
                            </Flex>
                        </Dialog.Body>
                        <Dialog.Footer>
                            <Button variant="outline" onClick={fetchSavedAnalyses} loading={savesLoading}>
                                Refresh
                            </Button>
                            <Button onClick={() => dispatchReverseDcf({ type: 'setField', field: 'savedListOpen', value: false })}>
                                Close
                            </Button>
                        </Dialog.Footer>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    );
}

function useReverseDcfController() {
    const { ticker } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0();
    const [reverseDcfState, dispatchReverseDcf] = useReducer(reverseDcfReducer, reverseDcfInitialState);
    const {
        marketCap,
        sharePrice,
        sharesOutstanding,
        netDebt,
        fcfTtm,
        years,
        wacc,
        terminalGrowth,
        resultsExpanded,
        marketCapExpanded,
        focusedInput,
        marketCapDraft,
        result,
        loading,
        error,
        longTermDebt,
        cashAndEquivalents,
        cashFlowMetadata,
        longTermDebtMetadata,
        cashMetadata,
        operatingCashFlow,
        capitalExpenditures,
        saveDialogOpen,
        savedListOpen,
        saveName,
        savedAnalyses,
        savesLoading,
        saveSubmitting,
        deletingSaveId,
        savesError,
        hiddenSeries,
        cashFlowView,
    } = reverseDcfState;

    const apiUrl = process.env.REACT_APP_API_URL;
    const currentCalendarYear = new Date().getFullYear();
    const initialDataState = useReverseDcfInitialData(apiUrl, ticker, currentCalendarYear);
    const initialLoading = initialDataState.loading;
    const stockPriceState = useStockPrice(apiUrl, ticker);
    const stockPriceResponse = stockPriceState.data;
    const initialSharesOutstanding = initialDataState.data?.sharesOutstanding;
    const stockPrice = {
        ...(stockPriceResponse || {}),
        shares_outstanding: stockPriceResponse?.shares_outstanding
            ?? initialSharesOutstanding?.value
            ?? null,
        shares_outstanding_metadata: stockPriceResponse?.shares_outstanding_metadata
            ?? initialSharesOutstanding?.metadata
            ?? null,
    };
    const priceLoading = stockPriceState.loading;
    const effectiveSharePrice = sharePrice || (stockPrice?.close != null ? String(stockPrice.close) : '');
    const effectiveSharesOutstanding = sharesOutstanding
        || (stockPrice?.shares_outstanding != null ? String(stockPrice.shares_outstanding) : '');
    const calculatedMarketCap = (() => {
        const parsedSharePrice = parseFloat(parseNumberFromFormatted(effectiveSharePrice));
        const parsedSharesOutstanding = parseFloat(parseNumberFromFormatted(effectiveSharesOutstanding));
        if (
            Number.isFinite(parsedSharePrice)
            && parsedSharePrice > 0
            && Number.isFinite(parsedSharesOutstanding)
            && parsedSharesOutstanding > 0
        ) {
            return toPlainNumberString(Math.round(parsedSharePrice * parsedSharesOutstanding));
        }
        return '';
    })();
    const effectiveMarketCap = marketCap
        || (stockPrice?.market_cap ? String(stockPrice.market_cap) : '')
        || calculatedMarketCap;
    const priceUnavailable = !priceLoading
        && !effectiveSharePrice
        && Boolean(stockPriceState.error || stockPriceResponse?.price_status === 'unavailable');

    const buildAssumptions = useCallback(() => ({
        marketCap: effectiveMarketCap,
        sharePrice: effectiveSharePrice,
        sharesOutstanding: effectiveSharesOutstanding,
        netDebt,
        fcfTtm,
        years,
        wacc,
        terminalGrowth,
    }), [effectiveMarketCap, effectiveSharePrice, effectiveSharesOutstanding, netDebt, fcfTtm, years, wacc, terminalGrowth]);

    const applyAssumptions = useCallback((assumptions) => {
        dispatchReverseDcf({ type: 'applyAssumptions', assumptions });
    }, []);

    const getAuthHeaders = useCallback(async () => {
        try {
            const token = await getAccessTokenSilently({ detailedResponse: false });
            return { Authorization: `Bearer ${token}` };
        } catch (err) {
            if (isRecoverableAuthError(err)) {
                await loginWithRedirect(getLoginRedirectOptions());
            }
            throw err;
        }
    }, [getAccessTokenSilently, loginWithRedirect]);

    const fetchSavedAnalyses = useCallback(async () => {
        if (!isAuthenticated || !ticker) return;

        dispatchReverseDcf({ type: 'setField', field: 'savesLoading', value: true });
        dispatchReverseDcf({ type: 'setField', field: 'savesError', value: '' });
        try {
            const headers = await getAuthHeaders();
            const { data } = await axios.get(
                `${apiUrl}/api/reverse-dcf/saves?ticker=${encodeURIComponent(ticker)}`,
                { headers }
            );
            dispatchReverseDcf({ type: 'setField', field: 'savedAnalyses', value: Array.isArray(data) ? data : data?.saves || [] });
        } catch (err) {
            if (isRecoverableAuthError(err)) return;
            const message = extractApiErrorMessage(err, 'Could not load saved analyses.');
            console.error('Failed to load reverse DCF saves:', {
                status: err.response?.status,
                data: err.response?.data,
                message: err.message,
                error: err,
            });
            dispatchReverseDcf({ type: 'setField', field: 'savesError', value: message });
        } finally {
            dispatchReverseDcf({ type: 'setField', field: 'savesLoading', value: false });
        }
    }, [apiUrl, getAuthHeaders, isAuthenticated, ticker]);

    const openSavedAnalyses = useCallback(async () => {
        dispatchReverseDcf({ type: 'setField', field: 'savedListOpen', value: true });
        await fetchSavedAnalyses();
    }, [fetchSavedAnalyses]);

    const saveCurrentAssumptions = useCallback(async () => {
        if (!isAuthenticated || !ticker) return;

        dispatchReverseDcf({ type: 'setField', field: 'saveSubmitting', value: true });
        dispatchReverseDcf({ type: 'setField', field: 'savesError', value: '' });
        try {
            const headers = await getAuthHeaders();
            const { data } = await axios.post(
                `${apiUrl}/api/reverse-dcf/saves`,
                {
                    ticker,
                    name: saveName.trim() || null,
                    assumptions: buildAssumptions(),
                },
                { headers }
            );
            dispatchReverseDcf({
                type: 'setField',
                field: 'savedAnalyses',
                value: (prev) => [data, ...prev.filter((item) => item.id !== data.id)].slice(0, 50),
            });
            dispatchReverseDcf({ type: 'setField', field: 'saveName', value: '' });
            dispatchReverseDcf({ type: 'setField', field: 'saveDialogOpen', value: false });
            toast.success('Reverse DCF assumptions saved.');
        } catch (err) {
            if (isRecoverableAuthError(err)) return;
            const message = extractApiErrorMessage(err, 'Could not save assumptions.');
            console.error('Failed to save reverse DCF assumptions:', {
                status: err.response?.status,
                data: err.response?.data,
                message: err.message,
                error: err,
            });
            dispatchReverseDcf({ type: 'setField', field: 'savesError', value: message });
            toast.error(message);
        } finally {
            dispatchReverseDcf({ type: 'setField', field: 'saveSubmitting', value: false });
        }
    }, [apiUrl, buildAssumptions, getAuthHeaders, isAuthenticated, saveName, ticker]);

    const loadSavedAnalysis = useCallback((saved) => {
        applyAssumptions(saved.assumptions);
        dispatchReverseDcf({ type: 'setField', field: 'savedListOpen', value: false });
        toast.success('Saved assumptions loaded.');
    }, [applyAssumptions]);

    const deleteSavedAnalysis = useCallback(async (saved) => {
        const label = saved.name || 'this saved analysis';
        if (!window.confirm(`Delete ${label}?`)) return;

        dispatchReverseDcf({ type: 'setField', field: 'deletingSaveId', value: saved.id });
        dispatchReverseDcf({ type: 'setField', field: 'savesError', value: '' });
        try {
            const headers = await getAuthHeaders();
            await axios.delete(`${apiUrl}/api/reverse-dcf/saves/${encodeURIComponent(saved.id)}`, { headers });
            dispatchReverseDcf({
                type: 'setField',
                field: 'savedAnalyses',
                value: (prev) => prev.filter((item) => item.id !== saved.id),
            });
            toast.success('Saved analysis deleted.');
        } catch (err) {
            if (isRecoverableAuthError(err)) return;
            const message = extractApiErrorMessage(err, 'Could not delete saved analysis.');
            console.error('Failed to delete reverse DCF save:', {
                status: err.response?.status,
                data: err.response?.data,
                message: err.message,
                error: err,
            });
            dispatchReverseDcf({ type: 'setField', field: 'savesError', value: message });
            toast.error(message);
        } finally {
            dispatchReverseDcf({ type: 'setField', field: 'deletingSaveId', value: null });
        }
    }, [apiUrl, getAuthHeaders]);

    const formatSavedAnalysisLabel = useCallback((saved) => {
        if (saved.name) return saved.name;
        const rawDate = saved.updated_at || saved.created_at;
        const date = rawDate ? new Date(rawDate) : null;
        const dateLabel = date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString() : 'No date';
        return `Unnamed - ${dateLabel}`;
    }, []);

    useEffect(() => {
        const initialData = initialDataState.data;
        if (!initialData) return;

        dispatchReverseDcf({ type: 'applyInitialData', initialData });
    }, [initialDataState.data]);

    const calculateReverseDcf = useCallback(async () => {
        // Parse formatted numbers (remove commas) before checking
        const parsedMarketCap = parseNumberFromFormatted(effectiveMarketCap);
        const parsedFcfTtm = parseNumberFromFormatted(fcfTtm, true);
        const parsedYears = isBlankNumberInput(years) ? NaN : Number(years);
        const parsedWacc = isBlankNumberInput(wacc) ? NaN : Number(wacc);
        const parsedTerminalGrowth = isBlankNumberInput(terminalGrowth) ? NaN : Number(terminalGrowth);
        
        // Check if required fields have values (allow negative for FCF)
        if (
            !parsedMarketCap
            || !parsedFcfTtm
            || parseFloat(parsedMarketCap) <= 0
            || !Number.isFinite(parsedYears)
            || !Number.isFinite(parsedWacc)
            || !Number.isFinite(parsedTerminalGrowth)
        ) {
            // Don't show error, just don't calculate if fields are empty or market cap is invalid
            dispatchReverseDcf({ type: 'calculationInvalid' });
            return;
        }

        dispatchReverseDcf({ type: 'calculationStarted' });

        // Clamp to UI-enforced ranges before posting, since auto-calculate can
        // fire while a field is still focused (before blur normalization runs).
        const safeYears = normalizeForecastYearsInput(parsedYears);
        const safeWacc = normalizeWaccInput(parsedWacc);
        const safeTerminalGrowth = normalizeTerminalGrowthInput(parsedTerminalGrowth);

        try {
            const parsedNetDebt = parseFloat(parseNumberFromFormatted(netDebt, true)) || 0;

            const response = await axios.post(`${apiUrl}/api/reverse-dcf`, {
                market_cap: parseFloat(parsedMarketCap),
                net_debt: parsedNetDebt,
                fcf_ttm: parseFloat(parsedFcfTtm),
                years: safeYears,
                wacc: safeWacc / 100,
                terminal_growth: safeTerminalGrowth / 100,
                ticker: ticker
            });

            dispatchReverseDcf({ type: 'calculationSucceeded', result: response.data });
        } catch (err) {
            dispatchReverseDcf({
                type: 'calculationFailed',
                error: err.response?.data?.error || 'Failed to calculate reverse DCF',
            });
        }
    }, [effectiveMarketCap, netDebt, fcfTtm, years, wacc, terminalGrowth, apiUrl, ticker]);

    // Auto-calculate when inputs change (if all required fields are filled)
    useEffect(() => {
        if (!initialLoading) {
            const timer = setTimeout(() => {
                calculateReverseDcf();
            }, 500); // Debounce
            return () => clearTimeout(timer);
        }
    }, [effectiveMarketCap, netDebt, fcfTtm, years, wacc, terminalGrowth, initialLoading, calculateReverseDcf]);

    const chartTimelineData = useMemo(() => {
        if (!result) return null;

        const points = [];
        const historicalFcf = result.historical_fcf || [];
        const projectionFcf = result.fcf_forecast || [];
        const terminalFcf = result.terminal_fcf_detail || [];

        const baseYear = currentCalendarYear;

        // Historical: real calendar years
        historicalFcf.forEach((h, idx) => {
            const isLast = idx === historicalFcf.length - 1;
            points.push({
                label: String(h.year),
                calendarYear: h.year,
                historicalFcf: h.fcf,
                historicalOcf: h.ocf ?? null,
                historicalCapex: h.capex ?? null,
                bridgeFcf: isLast ? h.fcf : null,
                projectedFcf: null,
                projectedPv: null,
                terminalFcf: null,
                terminalPv: null,
                section: 'historical',
            });
        });

        // "Now" = present (base FCF), bridges historical to projection
        const parsedFcf = parseFloat(parseNumberFromFormatted(fcfTtm, true));
        if (!isNaN(parsedFcf)) {
            points.push({
                label: 'Now',
                calendarYear: baseYear,
                historicalFcf: null,
                bridgeFcf: parsedFcf,
                projectedFcf: parsedFcf,
                projectedPv: null,
                terminalFcf: null,
                terminalPv: null,
                section: 'bridge',
            });
        }

        // Projection period
        projectionFcf.forEach((f) => {
            points.push({
                label: String(baseYear + f.year),
                calendarYear: baseYear + f.year,
                historicalFcf: null,
                projectedFcf: f.fcf,
                projectedPv: f.pv_fcf,
                terminalFcf: null,
                terminalPv: null,
                section: 'projection',
            });
        });

        // Bridge: connect last projection to first terminal
        const lastProjection = projectionFcf.length > 0 ? projectionFcf[projectionFcf.length - 1] : null;
        const firstTerminal = terminalFcf.length > 0 ? terminalFcf[0] : null;
        if (lastProjection && firstTerminal) {
            const lastProjIdx = points.length - 1;
            points[lastProjIdx].terminalFcf = lastProjection.fcf;
            points[lastProjIdx].terminalPv = lastProjection.pv_fcf;
        }

        // Terminal period
        terminalFcf.forEach((f) => {
            points.push({
                label: String(baseYear + f.year),
                calendarYear: baseYear + f.year,
                historicalFcf: null,
                projectedFcf: null,
                projectedPv: null,
                terminalFcf: f.fcf,
                terminalPv: f.pv_fcf,
                section: 'terminal',
            });
        });

        return points;
    }, [result, fcfTtm, currentCalendarYear]);

    const toggleSeries = (dataKey) => {
        dispatchReverseDcf({
            type: 'setField',
            field: 'hiddenSeries',
            value: (prev) => ({ ...prev, [dataKey]: !prev[dataKey] }),
        });
    };

    const filteredChartData = useMemo(() => {
        if (!chartTimelineData) return null;
        const hideHistorical = !!hiddenSeries.historicalFcf;
        const hideTerminal = !!hiddenSeries.terminalFcf && !!hiddenSeries.terminalPv;
        return chartTimelineData.filter((p) => {
            if (hideHistorical && p.section === 'historical') return false;
            if (hideTerminal && p.section === 'terminal') return false;
            return true;
        });
    }, [chartTimelineData, hiddenSeries]);

    const renderHistoricalFcfDot = useCallback(
        (props, { active = false } = {}) => {
            const { cx, cy, payload } = props;
            if (
                cx == null
                || cy == null
                || !payload
                || payload.section !== 'historical'
                || payload.historicalFcf == null
                || payload.calendarYear == null
            ) {
                return null;
            }
            const year = payload.calendarYear;
            const path = `/stock/${String(ticker || '').toLowerCase()}/financials/statement-of-cash-flows?year=${encodeURIComponent(String(year))}`;
            const rOuter = active ? 14 : 12;
            const rInner = active ? 5 : 3;
            return (
                <g key={`historical-fcf-${active ? 'active' : 'dot'}-${year}`}>
                    <title>{`Statement of Cash Flows (${year}) — click to open`}</title>
                    <circle
                        cx={cx}
                        cy={cy}
                        r={rOuter}
                        fill="transparent"
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate(path);
                        }}
                    />
                    <circle
                        cx={cx}
                        cy={cy}
                        r={rInner}
                        fill={active ? '#718096' : '#A0AEC0'}
                        stroke={active ? '#fff' : 'none'}
                        strokeWidth={active ? 1 : 0}
                        pointerEvents="none"
                    />
                </g>
            );
        },
        [ticker, navigate],
    );

    const historicalFcfDot = useCallback((props) => renderHistoricalFcfDot(props, { active: false }), [renderHistoricalFcfDot]);
    const historicalFcfActiveDot = useCallback((props) => renderHistoricalFcfDot(props, { active: true }), [renderHistoricalFcfDot]);

    const chartSectionBounds = useMemo(() => {
        if (!filteredChartData) return {};
        const historical = filteredChartData.filter(p => p.section === 'historical');
        const projections = filteredChartData.filter(p => p.section === 'projection' || p.section === 'bridge');
        const terminal = filteredChartData.filter(p => p.section === 'terminal');
        return {
            historicalStart: historical.length > 0 ? historical[0].label : null,
            historicalEnd: historical.length > 0 ? historical[historical.length - 1].label : null,
            projectionStart: projections.length > 0 ? projections[0].label : null,
            projectionEnd: projections.length > 0 ? projections[projections.length - 1].label : null,
            terminalStart: terminal.length > 0 ? terminal[0].label : null,
            terminalEnd: terminal.length > 0 ? terminal[terminal.length - 1].label : null,
        };
    }, [filteredChartData]);

    const labelStyles = { mt: '2', ml: '-2.5', fontSize: 'sm' };
    const isTerminalGrowthOutsideComfortRange = terminalGrowth < TERMINAL_GROWTH_COMFORT_RANGE.min
        || terminalGrowth > TERMINAL_GROWTH_COMFORT_RANGE.max;
    const isTerminalGrowthExtreme = terminalGrowth < TERMINAL_GROWTH_EXTREME_NEGATIVE
        || terminalGrowth > TERMINAL_GROWTH_EXTREME_POSITIVE;

    return {
        ticker,
        initialLoading,
        isAuthenticated,
        loginWithRedirect,
        openSavedAnalyses,
        savesLoading,
        stockPrice,
        effectiveSharePrice,
        effectiveSharesOutstanding,
        effectiveMarketCap,
        marketCapExpanded,
        marketCapDraft,
        focusedInput,
        dispatchReverseDcf,
        priceLoading,
        priceUnavailable,
        netDebt,
        longTermDebt,
        cashAndEquivalents,
        longTermDebtMetadata,
        cashMetadata,
        fcfTtm,
        operatingCashFlow,
        capitalExpenditures,
        cashFlowMetadata,
        years,
        wacc,
        terminalGrowth,
        labelStyles,
        isTerminalGrowthOutsideComfortRange,
        isTerminalGrowthExtreme,
        error,
        loading,
        result,
        resultsExpanded,
        filteredChartData,
        hiddenSeries,
        cashFlowView,
        toggleSeries,
        chartSectionBounds,
        historicalFcfDot,
        historicalFcfActiveDot,
        saveDialogOpen,
        saveName,
        savesError,
        saveSubmitting,
        saveCurrentAssumptions,
        savedListOpen,
        savedAnalyses,
        loadSavedAnalysis,
        formatSavedAnalysisLabel,
        deleteSavedAnalysis,
        deletingSaveId,
        fetchSavedAnalyses,
    };
}

function StockReverseDcf() {
    const controller = useReverseDcfController();

    return (
        <Box w="100%">
            <Box display="flex" flexDirection="column" w="100%" pr={{ base: 0, lg: 2 }}>
                <Text fontSize="2xl" fontWeight="bold" mb={4}>
                    Reverse DCF for {controller.ticker?.toUpperCase()}
                </Text>
                <Text fontSize="md" mb={6} color="gray.600">
                    Solve for the implied FCF growth rate given today's market price.
                </Text>

                <Flex
                    justifyContent="space-around"
                    align={{ base: 'flex-start', lg: 'flex-start' }}
                    gap={{ base: 8, lg: 5 }}
                    direction={{ base: 'column', lg: 'row' }}
                    w="100%"
                >
                    <ReverseDcfInputs
                        initialLoading={controller.initialLoading}
                        isAuthenticated={controller.isAuthenticated}
                        loginWithRedirect={controller.loginWithRedirect}
                        openSavedAnalyses={controller.openSavedAnalyses}
                        savesLoading={controller.savesLoading}
                        ticker={controller.ticker}
                        stockPrice={controller.stockPrice}
                        effectiveSharePrice={controller.effectiveSharePrice}
                        effectiveSharesOutstanding={controller.effectiveSharesOutstanding}
                        effectiveMarketCap={controller.effectiveMarketCap}
                        marketCapExpanded={controller.marketCapExpanded}
                        marketCapDraft={controller.marketCapDraft}
                        focusedInput={controller.focusedInput}
                        dispatchReverseDcf={controller.dispatchReverseDcf}
                        priceLoading={controller.priceLoading}
                        priceUnavailable={controller.priceUnavailable}
                        netDebt={controller.netDebt}
                        longTermDebt={controller.longTermDebt}
                        cashAndEquivalents={controller.cashAndEquivalents}
                        longTermDebtMetadata={controller.longTermDebtMetadata}
                        cashMetadata={controller.cashMetadata}
                        fcfTtm={controller.fcfTtm}
                        operatingCashFlow={controller.operatingCashFlow}
                        capitalExpenditures={controller.capitalExpenditures}
                        cashFlowMetadata={controller.cashFlowMetadata}
                        years={controller.years}
                        wacc={controller.wacc}
                        terminalGrowth={controller.terminalGrowth}
                        labelStyles={controller.labelStyles}
                        isTerminalGrowthOutsideComfortRange={controller.isTerminalGrowthOutsideComfortRange}
                        isTerminalGrowthExtreme={controller.isTerminalGrowthExtreme}
                        error={controller.error}
                    />
                    <ReverseDcfResults
                        loading={controller.loading}
                        result={controller.result}
                        resultsExpanded={controller.resultsExpanded}
                        dispatchReverseDcf={controller.dispatchReverseDcf}
                        filteredChartData={controller.filteredChartData}
                        hiddenSeries={controller.hiddenSeries}
                        cashFlowView={controller.cashFlowView}
                        toggleSeries={controller.toggleSeries}
                        chartSectionBounds={controller.chartSectionBounds}
                        historicalFcfDot={controller.historicalFcfDot}
                        historicalFcfActiveDot={controller.historicalFcfActiveDot}
                    />
                </Flex>

                <SaveAssumptionsDialog
                    saveDialogOpen={controller.saveDialogOpen}
                    dispatchReverseDcf={controller.dispatchReverseDcf}
                    saveName={controller.saveName}
                    savesError={controller.savesError}
                    saveSubmitting={controller.saveSubmitting}
                    saveCurrentAssumptions={controller.saveCurrentAssumptions}
                />
                <SavedAnalysesDialog
                    savedListOpen={controller.savedListOpen}
                    dispatchReverseDcf={controller.dispatchReverseDcf}
                    savesLoading={controller.savesLoading}
                    savesError={controller.savesError}
                    savedAnalyses={controller.savedAnalyses}
                    ticker={controller.ticker}
                    loadSavedAnalysis={controller.loadSavedAnalysis}
                    formatSavedAnalysisLabel={controller.formatSavedAnalysisLabel}
                    deleteSavedAnalysis={controller.deleteSavedAnalysis}
                    deletingSaveId={controller.deletingSaveId}
                    fetchSavedAnalyses={controller.fetchSavedAnalyses}
                />
            </Box>
        </Box>
    );
}

export default StockReverseDcf;
